import { DEFAULT_THEME_CONFIG, type ThemeBorder, type ThemeConfig, type ThemeDensity, type ThemeMode, type ThemePreset, THEME_PRESETS } from "@/shared/theme/lib/types";

export const THEME_STORAGE_KEY = "app-theme-v2";

function isMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

function isDensity(value: unknown): value is ThemeDensity {
  return value === "comfortable" || value === "compact";
}

function isBorder(value: unknown): value is ThemeBorder {
  return value === "subtle" || value === "normal" || value === "strong";
}

function isPreset(value: unknown): value is ThemePreset {
  return typeof value === "string" && value in THEME_PRESETS;
}

function sanitizeColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : fallback;
}

function sanitizeRadius(value: unknown): number {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return DEFAULT_THEME_CONFIG.radius;
  }
  return Math.min(1, Math.max(0.4, value));
}

function normalizeThemeConfig(value: unknown): ThemeConfig {
  if (!value || typeof value !== "object") {
    return DEFAULT_THEME_CONFIG;
  }

  const candidate = value as Partial<ThemeConfig>;
  const preset = isPreset(candidate.preset) ? candidate.preset : DEFAULT_THEME_CONFIG.preset;
  const presetColors = THEME_PRESETS[preset];

  return {
    version: 1,
    mode: isMode(candidate.mode) ? candidate.mode : DEFAULT_THEME_CONFIG.mode,
    preset,
    primary: sanitizeColor(candidate.primary, presetColors.primary),
    accent: sanitizeColor(candidate.accent, presetColors.accent),
    radius: sanitizeRadius(candidate.radius),
    density: isDensity(candidate.density) ? candidate.density : DEFAULT_THEME_CONFIG.density,
    border: isBorder(candidate.border) ? candidate.border : DEFAULT_THEME_CONFIG.border,
  };
}

export function loadThemeConfig(): ThemeConfig {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_CONFIG;
  }

  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_THEME_CONFIG;
    }
    return normalizeThemeConfig(JSON.parse(raw));
  } catch {
    return DEFAULT_THEME_CONFIG;
  }
}

export function saveThemeConfig(config: ThemeConfig): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(config));
}

export function clearThemeConfig(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(THEME_STORAGE_KEY);
}
